import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { BLOCK_REGISTRY } from "@/components/blocks";
import { SponsorPopup } from "@/components/microsite/SponsorPopup";
import { MicrositeNav } from "@/components/microsite/MicrositeNav";
import { HowToBid } from "@/components/website/HowToBid";
import { WebsiteFooter } from "@/components/website/WebsiteFooter";
import { eventBase, useBrandedRedirect } from "@/lib/microsite";
import { EmptyState } from "@/components/EmptyState";
import { PrivateGate } from "@/components/PrivateGate";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventSocket, useUserSocket } from "@/hooks/useSockets";
import { api, mediaUrlFromMap } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { localized } from "@/lib/intl";
import { useJsonLd, usePageMeta, PUBLIC_BASE } from "@/lib/seo";
import { brandStyle, radiusAttr } from "@/lib/theme";
import { ensurePairingLoaded } from "@/lib/fonts";
import { BRAND } from "@/config/brand";
import { SearchX } from "lucide-react";

export default function EventLanding() {
  const { slug: legacySlug, eventSlug } = useParams();
  const slug = eventSlug || legacySlug;
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(undefined); // undefined=loading, null=not found
  // Estado PERSONAL por lote del comprador logueado (leading/outbid/watching),
  // derivado del endpoint existente /me/bids — jamás para anónimos.
  const [personalLots, setPersonalLots] = useState(null);

  const loadPersonal = useCallback(() => {
    if (!user) {
      setPersonalLots(null);
      return;
    }
    api
      .get("/me/bids")
      .then(({ data: d }) => {
        const map = {};
        (d.active || []).forEach((c) => { map[c.lot.id] = c.leading ? "leading" : "outbid"; });
        (d.watching || []).forEach((c) => { map[c.lot.id] = "watching"; });
        setPersonalLots(map);
      })
      .catch(() => setPersonalLots(null));
  }, [user]);

  useEffect(loadPersonal, [loadPersonal]);

  // push personal existente (outbid/won/lost): refresca solo los badges — el
  // catálogo no se recarga (los precios ya llegan por el canal del evento)
  useUserSocket(
    useCallback((msg) => {
      if (msg.type === "notification") loadPersonal();
    }, [loadPersonal]),
    Boolean(user)
  );

  const load = useCallback(() => {
    api
      .get(`/public/events/${slug}`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
  }, [slug]);

  useEffect(() => {
    setData(undefined);
    load();
  }, [load]);

  // live prices in the catalog grid: merge lot updates pushed to the event room
  const onSocketMessage = useCallback((msg) => {
    if ((msg.type === "lot_update" || msg.type === "lot_closed") && msg.lot) {
      setData((prev) => {
        if (!prev?.lots) return prev;
        return {
          ...prev,
          lots: prev.lots.map((item) => (item.lot.id === msg.lot.id ? { ...item, lot: msg.lot } : item)),
        };
      });
    }
    // private gate: an approval arriving live unlocks the page without a refresh
    if (msg.type === "registration_update") {
      setData((prev) => {
        if (prev?.gated) load();
        return prev;
      });
    }
  }, [load]);
  const { offsetRef } = useEventSocket(data?.event?.id, onSocketMessage);

  const locale = i18n.resolvedLanguage;
  const theme = data?.theme || data?.organizer?.theme;
  useEffect(() => {
    if (theme?.font_pairing) ensurePairingLoaded(theme.font_pairing);
  }, [theme?.font_pairing]);
  usePageMeta({
    title: data?.event ? `${localized(data.event.title, locale)} — ${BRAND.name}` : undefined,
    description: data?.event ? localized(data.event.description, locale) : undefined,
    image: data?.event ? mediaUrlFromMap(data.media, data.event.hero_media_id) : undefined,
    imageAlt: data?.event ? localized(data.event.title, locale) : undefined,
    // WS6: canonical propio en la ruta branded; solo subastas PÚBLICAS
    // indexables (ended se conserva como archivo); privadas/unlisted → noindex.
    url: data?.event && data?.organizer?.slug
      ? `${PUBLIC_BASE}/${data.organizer.slug}/${data.event.slug}`
      : undefined,
    robots: data?.event && data.event.visibility !== "public" ? "noindex, nofollow" : undefined,
    ogType: "website",
  });
  useJsonLd(
    data?.event
      ? {
          "@context": "https://schema.org",
          "@type": "Event",
          name: localized(data.event.title, locale),
          description: localized(data.event.description, locale) || undefined,
          startDate: data.event.starts_at || undefined,
          endDate: data.event.ends_at || undefined,
          eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
          location: {
            "@type": "VirtualLocation",
            // WS15: URL canónica branded sobre PUBLIC_BASE (sin origin del preview).
            url: data.organizer?.slug
              ? `${PUBLIC_BASE}/${data.organizer.slug}/${data.event.slug}`
              : `${PUBLIC_BASE}${window.location.pathname}`,
          },
          // Posicionamiento: la subasta la organiza el ORGANIZADOR (cliente),
          // nunca AOS Equestrian — AOS es solo la plataforma tecnológica.
          organizer: data.organizer?.name ? { "@type": "Organization", name: data.organizer.name } : undefined,
          image: mediaUrlFromMap(data.media, data.event.hero_media_id) || undefined,
        }
      : null
  );

  useBrandedRedirect(data?.organizer?.slug, data?.event?.slug);

  if (data === undefined) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 flex flex-col gap-8" data-testid="landing-skeleton">
        <Skeleton className="h-96 rounded-3xl" />
        <Skeleton className="h-8 w-1/2 rounded-xl" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState
          icon={SearchX}
          testid="landing-not-found"
          title={t("pub.emptyCatalogTitle")}
          description={t("pub.emptyCatalogDesc")}
        />
      </div>
    );
  }

  if (data.gated) {
    return <PrivateGate data={data} onReload={load} />;
  }

  /* Phase 6 — controlled structure: the mandatory auction sections ALWAYS
     exist even if the organizer's landing config omits them. Hero leads,
     the collection, auction terms and legal footer can never disappear. */
  const blocks = [...(data.event.landing_config || []).filter((b) => b.visible)];
  if (!blocks.some((b) => b.type === "hero")) blocks.unshift({ id: "auto-hero", type: "hero", props: {}, visible: true });
  for (const type of ["lots_grid", "legal_terms"]) {
    if (!blocks.some((b) => b.type === type)) blocks.push({ id: `auto-${type}`, type, props: {}, visible: true });
  }
  const liveAuction = (data.auctions || []).find((a) => ["live", "hybrid"].includes(a.type));
  const base = eventBase(data.organizer?.slug, slug);

  /* C1: microsite section menu — derived from the enabled sections, anchors only */
  const NAV_SECTION_KEYS = {
    lots_grid: "sections.catalogue",
    how_it_works: "sections.howItWorks",
    faq: "sections.faq",
    conditions: "sections.conditions",
    legal_terms: "sections.legalTerms",
    register_horse: "sections.registerHorse",
    contact: "sections.contact",
  };
  const navItems = blocks.filter((b) => NAV_SECTION_KEYS[b.type]);

  const scrollToSection = (e, type) => {
    e.preventDefault();
    document.getElementById(`sec-${type}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="event-landing-page" className="brand-scope" data-radius={radiusAttr(theme)} style={brandStyle(theme)}>
      <MicrositeNav
        organizer={data.organizer}
        media={data.media}
        theme={theme}
        sections={navItems.map((b) => ({ id: b.id, type: b.type, label: t(NAV_SECTION_KEYS[b.type]) }))}
        onSectionClick={scrollToSection}
        shareTitle={localized(data.event.title, locale)}
        registerTo={data.event.status !== "ended" ? `${base}/register` : null}
      />
      {liveAuction && (
        <div className="max-w-6xl mx-auto px-6 lg:px-12 mt-6">
          <Link
            to={`${base}/live`}
            data-testid="event-live-room-banner"
            className="group flex items-center gap-4 rounded-2xl bg-[#0A0A0A] px-6 py-5 hover:bg-black/85 transition-colors duration-300"
          >
            <span className="relative flex w-2.5 h-2.5 shrink-0" aria-hidden="true">
              <span className={`absolute inline-flex w-full h-full rounded-full opacity-60 ${liveAuction.status === "live" ? "bg-[#E14434] animate-ping" : "bg-white/50"}`} />
              <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${liveAuction.status === "live" ? "bg-[#E14434]" : "bg-white/70"}`} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-white font-semibold text-sm sm:text-base">
                {liveAuction.status === "live" ? t("live.roomBannerLiveTitle") : t("live.roomBannerTitle")}
              </span>
              <span className="block text-white/55 text-xs sm:text-sm mt-0.5 truncate">{t("live.roomBannerDesc")}</span>
            </span>
            <span className="shrink-0 inline-flex items-center h-10 px-5 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold group-hover:bg-white/85 transition-colors duration-300">
              {t("live.enterRoom")}
            </span>
          </Link>
        </div>
      )}
      {blocks.map((block) => {
        const Component = BLOCK_REGISTRY[block.type];
        if (!Component) return null;
        return (
          <div key={block.id} id={`sec-${block.type}`} className="scroll-mt-12">
            <Component data={{ ...data, personalLots, offsetRef }} props={block.props} />
          </div>
        );
      })}
      {/* Auto-display (client review block 3): the event HAS sponsors but the
          organizer never added the block — render the sponsor wall anyway. */}
      {data.sponsors?.length > 0 && !blocks.some((b) => b.type === "sponsors") && (
        <div id="sec-sponsors" className="scroll-mt-12" data-testid="sponsors-auto-section">
          {(() => {
            const Sponsors = BLOCK_REGISTRY.sponsors;
            return <Sponsors data={data} />;
          })()}
        </div>
      )}
      <HowToBid website={data.website} browseAnchor="sec-lots_grid" registerTo={data.event.status !== "ended" ? `${base}/register` : null} />
      <WebsiteFooter organizer={data.organizer} website={data.website} media={data.media} eventId={data.event.id} />
      <SponsorPopup data={data} />
    </div>
  );
}
